import { TopicCategoryPicker } from './TopicCategoryPicker';
import React, { useEffect, useId, useState } from 'react';
import {
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
  FolderIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { VideoCategory, VideoFormData } from '../../types/video';
import { ProductTypeLabels, videoProductTypes } from '../../types/productType';
import {
  ContentStatus,
  HeadphonesRecommendation,
  HeadphonesLabels,
  STATUS_ANZEIGE,
  STATUS_WAEHLBAR
} from '../../types';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { artistService } from '../../services/artistService';
import { Artist } from '../../types/artist';
import { SchlagwortFeld } from './SchlagwortFeld';
import { ModerationsHinweis } from './ModerationsHinweis';
import { PremiumFeld } from './PremiumFeld';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { useDarfModerieren } from '../../hooks/useDarfModerieren';
import { fälltAufEntwurf, MODERATIONS_TEXTE, RÜCKFALL_RÜCKFRAGE, stufeGesperrt, stufenText } from '../../utils/moderation';

interface VideoFormProps {
  initialData?: Partial<VideoFormData>;
  onSubmit: (data: VideoFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  // Nur im Edit-Modus gesetzt: ID des geladenen Videos + Lösch-Handler (Gefahren-Aktion).
  videoId?: string;
  onDelete?: (id: string) => void;
  /**
   * ⚠️ Hier stand die Eigenschaft `notify` — der Weg, über den dieses Formular in den Balken von
   * `VideoManagement` meldete. Sie ist am 2026-09-08 ENTFALLEN, zusammen mit ihrem einzigen
   * Anlass: der Sperre „Bitte warten, bis alle Uploads abgeschlossen sind". Die drei
   * Upload-Bereiche sind mit Toms Entscheid gegangen, und damit der einzige Fall, den kein
   * einzelnes Feld tragen konnte.
   *
   * ⛔ Bewusst NICHT als unbenutzte Eigenschaft stehengelassen, nur damit alle fünf Formulare
   * gleich aussehen: `AudioForm`, `AlbumForm`, `ArtistForm` und `PlaylistForm` tragen sie
   * weiter, weil sie sie BENUTZEN. Der Gleichklang der Namen war nie der Grund für ihre
   * Existenz — das war der eine Fall, und den gibt es hier nicht mehr. Kommt er wieder, heisst
   * die Eigenschaft wieder `notify` und nicht anders.
   */
}

export const VideoForm: React.FC<VideoFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  isLoading = false,
  videoId,
  onDelete
}) => {
  const buildFormData = (data: Partial<VideoFormData>): VideoFormData => ({
    category: data.category,
    title: data.title || '',
    description: data.description || '',
    shortDescription: data.shortDescription || '',
    duration: data.duration || '',
    headphones: data.headphones || HeadphonesRecommendation.NO,
    topicCategories: data.topicCategories || [],
    videoUrl: data.videoUrl || '',
    thumbnailUrl: data.thumbnailUrl || '',
    coverImageUrl: data.coverImageUrl || '',
    // Affirmationen als Roh-String (eine pro Zeile) — genau wie in AudioForm. Das Aufteilen zu
    // string[] macht erst der Dienst; hier bleibt der Text so stehen, wie er getippt wurde.
    affirmations: data.affirmations || '',
    // Intro-Ende als Roh-String — leer bleibt leer. Zur Zahl (oder null) macht es erst der Dienst.
    introEndSeconds: data.introEndSeconds || '',
    // Anzeige-Spiegel des Interpreten. Kein eigenes Eingabefeld — gewählt wird über `artistId`,
    // den Namen leitet das Backend daraus ab. Durchgereicht, damit ein Save ihn nicht leert.
    artist: data.artist || '',
    artistId: data.artistId || '', // kuratierter Interpret (Dropdown); '' = kein Interpret
    album: data.album || '',
    language: data.language || '',
    // Premium ueberall (Tom 2026-09-11): neu → Premium; ein freies Werk bleibt frei.
    isPremium: data.isPremium ?? true,
    status: data.status || ContentStatus.DRAFT,
    tags: data.tags || []
  });

  // Siehe `ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten. Feste `id`-Werte
  // wären beim zweiten Formular im selben Baum doppelt und brächen `htmlFor` still.
  const feldId = useId();
  // ⚠️ Der Satz „ersetzt `document.getElementById('tagInput')`" stand hier bis 2026-09-08 und
  // beschrieb das Tag-Feld. Das wohnt jetzt in `SchlagwortFeld` und erzeugt seine Kennung dort
  // selbst — mit demselben `useId` und aus demselben Grund. Die Begruendung wandert also mit dem
  // Feld; `feldId` hier bleibt für die uebrigen Beschriftungen dieses Formulars.

  const [formData, setFormData] = useState<VideoFormData>(
    buildFormData(initialData)
  );

  // Was der Moderation gehört (Bezahlschranke, Veröffentlichen, Rückfall auf den Entwurf) — Regeln und
  // Texte in `utils/moderation.ts`. Der Bestand ist der Status, mit dem das Formular geöffnet wurde.
  const darfModerieren = useDarfModerieren();
  const bestandStatus = initialData.status;
  const rückfall = fälltAufEntwurf(darfModerieren, bestandStatus, formData.status);
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Kuratierte Interpreten fürs Künstler-Dropdown (Admin-Liste, alle Status → auch Entwürfe wählbar).
  const [artists, setArtists] = useState<Artist[]>([]);
  useEffect(() => {
    let alive = true;
    artistService
      .getArtists()
      .then((list) => { if (alive) setArtists(list); })
      .catch(() => { /* Dropdown bleibt leer — Künstler bleibt optional */ });
    return () => { alive = false; };
  }, []);

  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    initialData.coverImage ? URL.createObjectURL(initialData.coverImage) : (initialData.coverImageUrl || null)
  );
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(
    initialData.thumbnail ? URL.createObjectURL(initialData.thumbnail) : (initialData.thumbnailUrl || null)
  );
  const [videoPreview, setVideoPreview] = useState<string | null>(
    initialData.videoUrl || null
  );
  const [showBunnyPicker, setShowBunnyPicker] = useState(false);
  const [bunnyPickerContext, setBunnyPickerContext] = useState<{
    type: 'image' | 'video';
    field?: 'cover' | 'thumbnail' | 'video';
  }>({ type: 'image', field: 'cover' });
  // HINWEIS: Kein useEffect auf [initialData] mehr!
  // Das Eltern-Component nutzt key={editingVideo?.id || 'new-video'}, wodurch
  // VideoForm bei Wechsel zwischen Neu/Bearbeiten komplett neu gemountet wird.
  // Der useState-Initialwert oben reicht aus.
  // Ein useEffect auf initialData führte dazu, dass bei jedem Re-Render des
  // Eltern-Components (z.B. Notification) die Formularfelder geleert wurden,
  // weil initialData={} jedes Mal eine neue Objekt-Referenz ist.

  const handleInputChange = <K extends keyof VideoFormData>(field: K, value: VideoFormData[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBunnyFileSelect = (url: string) => {
    const { type, field } = bunnyPickerContext;
    
    if (type === 'image') {
      if (field === 'cover') {
        setCoverImagePreview(url);
        setFormData(prev => ({ ...prev, coverImageUrl: url, coverImage: undefined }));
      } else if (field === 'thumbnail') {
        setThumbnailPreview(url);
        setFormData(prev => ({ ...prev, thumbnailUrl: url, thumbnail: undefined }));
      }
    } else if (type === 'video' && field === 'video') {
      setVideoPreview(url);
      setFormData(prev => ({ ...prev, videoUrl: url, videoFile: undefined }));
    }
  };

  const openBunnyPicker = (type: 'image' | 'video', field: 'cover' | 'thumbnail' | 'video') => {
    setBunnyPickerContext({ type, field });
    setShowBunnyPicker(true);
  };

  const removeFile = (type: 'cover' | 'thumbnail' | 'video') => {
    if (type === 'cover') {
      setFormData(prev => ({ ...prev, coverImage: undefined, coverImageUrl: undefined }));
      setCoverImagePreview(null);
    } else if (type === 'thumbnail') {
      setFormData(prev => ({ ...prev, thumbnail: undefined, thumbnailUrl: undefined }));
      setThumbnailPreview(null);
    } else if (type === 'video') {
      setFormData(prev => ({ ...prev, videoFile: undefined, videoUrl: undefined }));
      setVideoPreview(null);
    }
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ⚠️ Hier stand eine Sperre „Bitte warten, bis alle Uploads abgeschlossen sind" ueber drei
    // getrennte Upload-Bereiche. Sie ist mit den Uploads entfallen (2026-09-08, Tom-Entscheid:
    // die CMS-Aufrufer des Video-/Mediathek-Uploads gehen, der Ingest uebernimmt). Die
    // Mediathek-Auswahl, die geblieben ist, laedt nichts hoch — sie setzt eine Adresse, die es
    // schon gibt, und kann deshalb auch nicht „noch laufen".

    // VIDEO-URL — bewusst OHNE eigene Meldung, siehe `required` am Feld weiter unten.
    //
    // Hier stand `alert('Bitte lade eine Video-Datei hoch oder gib eine Video-URL an.')`. Das Feld
    // heißt bereits „Video URL *" und trägt jetzt `required`; damit lehnt der Browser das Absenden
    // selbst ab und schreibt seinen Hinweis AN das Feld — nachgemessen unter jsdom: 0 Aufrufe von
    // `onSubmit` bei leerem Pflichtfeld. Upload und Bunny-Wähler schreiben beide in dasselbe
    // `formData.videoUrl`, das dieses Eingabefeld anzeigt; ein Upload erfüllt die Pflicht also
    // genauso wie eine getippte URL.
    //
    // ⚠️ Die Leerzeichen-Lücke, die beim Titel in `AudioForm` bleibt, gibt es hier nicht:
    // `type="url"` weist „   " ohnehin ab, und zwar unabhängig von `required`.

    // F3: speichert die Redaktion ein veröffentlichtes Werk, fällt es auf den Entwurf — vorher fragen,
    // nicht hinterher melden. Begründung an `RÜCKFALL_RÜCKFRAGE`.
    if (rückfall && !(await frage(RÜCKFALL_RÜCKFRAGE))) return;
    onSubmit(formData);
  };


  // Der Parameter ist ein Slug aus der Datenbank, kein Enum-Wert mehr — die Taxonomie ist zur
  // Laufzeit pflegbar, ein Compile-Zeit-Typ kann sie nicht mehr abbilden. Die Gültigkeit
  // sichert stattdessen die Auswahl-Komponente (nur bekannte oder bereits gesetzte Slugs).
  const handleSubTopicToggle = (subTopic: string) => {
    setFormData(prev => {
      const currentCategories = prev.topicCategories || [];
      const isSelected = currentCategories.includes(subTopic);
      
      return {
        ...prev,
        topicCategories: isSelected
          ? currentCategories.filter(c => c !== subTopic)
          : [...currentCategories, subTopic]
      };
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6 pb-24">
      {/* Die Gliederung ist die von `AudioForm` — dieselben Karten, dieselben Klassen, damit beide
          Seiten sich gleich lesen. Es fehlen genau die beiden Varianten-Karten (Audio-Streams,
          zusätzliche Spuren): ein Video hat keine parallelen Tonspuren. An ihrer Stelle steht
          „Video & Bilder" mit Videodatei, Thumbnail und Poster.
          ⚠️ Die Reihenfolge der drei Upload-Bereiche (Video, Thumbnail, Poster) ist verbindlich:
          `VideoForm.test.tsx` greift den Video-Upload über `input[type="file"]`[0]. */}

        {/* Sektion 1: Grunddaten */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Grunddaten</h2>
          {/* Produktkategorie */}
          <div>
            <label htmlFor={`${feldId}-kategorie`} className="block text-sm font-medium text-secondary-700 mb-2">
              Produktkategorie *
            </label>
            <select
              id={`${feldId}-kategorie`}
              value={formData.category || ''}
              onChange={(e) => handleInputChange('category', e.target.value ? (e.target.value as VideoCategory) : undefined)}
              className="input w-full cursor-pointer"
              required
            >
              <option value="">Bitte wählen</option>
              {videoProductTypes.map(category => (
                <option key={category} value={category}>
                  {ProductTypeLabels[category]}
                </option>
              ))}
            </select>
          </div>

          {/* Titel */}
          <div>
            <label htmlFor={`${feldId}-titel`} className="block text-sm font-medium text-secondary-700 mb-2">
              Titel *
            </label>
            <input
              id={`${feldId}-titel`}
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="input w-full"
              placeholder="Video-Titel eingeben..."
              required
            />
          </div>

          {/* ── ZWEI Beschreibungen statt drei (Bereinigung 2026-09-01, Tom-Zielbild) ────────
              Die Lang-Box ist weg — und seit dem CONTRACT (#769) auch das Feld dahinter.
              Begründung ausführlich in `AudioForm`. */}
          {/* Beschreibung */}
          <div>
            <label htmlFor={`${feldId}-beschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
              Beschreibung
            </label>
            <textarea
              id={`${feldId}-beschreibung`}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className="input w-full"
              placeholder="Ausführliche Beschreibung — dieser Text steht auf der Produktseite."
            />
          </div>

          {/* Kurzbeschreibung */}
          <div>
            <label htmlFor={`${feldId}-kurzbeschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">
              Kurzbeschreibung
            </label>
            {/* ⚠️ Hier stand ein einzeiliges `<input type="text">`. Der Feldsatz ist derselbe wie
                bei Audio — eine Kurzbeschreibung ist dort seit jeher ein Textbereich über drei
                Zeilen. Ein einzeiliges Feld nimmt zwar denselben Text an, zeigt aber nur einen
                Ausschnitt davon; wer redigiert, scrollt seitwärts durch seinen eigenen Satz. */}
            <textarea
              id={`${feldId}-kurzbeschreibung`}
              value={formData.shortDescription || ''}
              onChange={(e) => handleInputChange('shortDescription', e.target.value)}
              rows={3}
              className="input w-full"
              placeholder="Kurze Beschreibung..."
            />
          </div>

          {/* Affirmationen — eine pro Zeile; Save splittet zu metadata.affirmations (string[]). */}
          <div>
            <label htmlFor={`${feldId}-affirmationen`} className="block text-sm font-medium text-secondary-700 mb-2">
              Affirmationen <span className="text-secondary-400">(eine pro Zeile)</span>
            </label>
            <textarea
              id={`${feldId}-affirmationen`}
              value={formData.affirmations || ''}
              onChange={(e) => handleInputChange('affirmations', e.target.value)}
              rows={8}
              className="input w-full"
              placeholder={'Ich liebe und akzeptiere meinen Körper, so wie er ist.\nMein Körper ist mein sicheres Zuhause.\n...'}
            />
          </div>
        </section>

        {/* Sektion 2: Video & Bilder (an der Stelle der Audio-Streams-Karte) */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Video &amp; Bilder</h2>
          {/* Video Upload / Video URL */}
          <div>
            {/* Ein Feld, nicht eine Gruppe: der Kasten darunter enthält nur Schaltflächen und ein
                `hidden` Datei-Feld — das Eingabefeld hier ist das einzige anspringbare Bedienelement
                und trägt den Wert, den auch Upload und Bunny hineinschreiben. */}
            <label htmlFor={`${feldId}-videourl`} className="block text-sm font-medium text-secondary-700 mb-2">
              Video URL *
            </label>
            {/* `required` ersetzt die frühere Prüfung in `handleSubmit` (dort begründet). Der Stern
                im Beschriftungstext stand schon vorher da — bis hierher hielt ihn nichts ein. */}
            <input
              id={`${feldId}-videourl`}
              type="url"
              value={formData.videoUrl || ''}
              onChange={(e) => {
                handleInputChange('videoUrl', e.target.value);
                setVideoPreview(e.target.value || null);
              }}
              className="input w-full mb-2"
              placeholder="https://... oder Upload/Bunny unten"
              required
            />
            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
              {videoPreview || formData.videoFile ? (
                <div className="relative">
                  <VideoCameraIcon className="mx-auto h-16 w-16 text-primary-600" />
                  <p className="mt-2 text-sm text-secondary-700">
                    {formData.videoFile?.name || videoPreview?.split('/').pop() || 'Video ausgewählt'}
                  </p>
                  {videoPreview && (
                    <div className="mt-4">
                      <video
                        controls
                        src={videoPreview}
                        className="mx-auto w-full max-w-xl rounded-lg border border-secondary-200"
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFile('video')}
                    className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <VideoCameraIcon className="mx-auto h-12 w-12 text-secondary-400" />
                  <div className="mt-2 space-y-2">
                    <button
                      type="button"
                      onClick={() => openBunnyPicker('video', 'video')}
                      className="btn btn-outline btn-sm"
                    >
                      <FolderIcon className="w-4 h-4 mr-2" />
                      Von Bunny.net
                    </button>
                    <p className="text-sm text-secondary-500 mt-1">
                      MP4 Format unterstützt
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Thumbnail URL */}
          <div>
            <label htmlFor={`${feldId}-thumbnailurl`} className="block text-sm font-medium text-secondary-700 mb-2">
              Thumbnail URL
            </label>
            <input
              id={`${feldId}-thumbnailurl`}
              type="url"
              value={formData.thumbnailUrl || ''}
              onChange={(e) => {
                handleInputChange('thumbnailUrl', e.target.value);
                setThumbnailPreview(e.target.value || null);
              }}
              className="input w-full mb-2"
              placeholder="https://..."
            />
            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
              {thumbnailPreview ? (
                <div className="relative">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail Preview"
                    className="mx-auto h-32 w-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile('thumbnail')}
                    className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <PhotoIcon className="mx-auto h-12 w-12 text-secondary-400" />
                  <div className="mt-2 space-y-2">
                    <button
                      type="button"
                      onClick={() => openBunnyPicker('image', 'thumbnail')}
                      className="btn btn-outline btn-sm"
                    >
                      <FolderIcon className="w-4 h-4 mr-2" />
                      Von Bunny.net
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Video Poster URL */}
          <div>
            <label htmlFor={`${feldId}-posterurl`} className="block text-sm font-medium text-secondary-700 mb-2">
              Video Poster URL
            </label>
            <input
              id={`${feldId}-posterurl`}
              type="url"
              value={formData.coverImageUrl || ''}
              onChange={(e) => {
                handleInputChange('coverImageUrl', e.target.value);
                setCoverImagePreview(e.target.value || null);
              }}
              className="input w-full mb-2"
              placeholder="https://..."
            />
            <div className="border-2 border-dashed border-secondary-300 rounded-lg p-6 text-center">
              {coverImagePreview ? (
                <div className="relative">
                  <img
                    src={coverImagePreview}
                    alt="Cover Preview"
                    className="mx-auto h-32 w-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile('cover')}
                    className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div>
                  <PhotoIcon className="mx-auto h-12 w-12 text-secondary-400" />
                  <div className="mt-2 space-y-2">
                    <button
                      type="button"
                      onClick={() => openBunnyPicker('image', 'cover')}
                      className="btn btn-outline btn-sm"
                    >
                      <FolderIcon className="w-4 h-4 mr-2" />
                      Von Bunny.net
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Sektion 3: Metadaten */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Metadaten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${feldId}-interpret`} className="block text-sm font-medium text-secondary-700 mb-2">
                Künstler / Interpret
              </label>
              {/* Auswahl, kein Freitext: der Interpret ist eine eigene Entität (content.artistId),
                  und ein getippter Name legt keine Zuordnung an — er stünde nur als Zeichenkette
                  daneben. Genau daher rührte „der Interpret wird nicht richtig angezeigt". */}
              <select
                id={`${feldId}-interpret`}
                value={formData.artistId || ''}
                onChange={(e) => handleInputChange('artistId', e.target.value)}
                className="input w-full cursor-pointer"
              >
                <option value="">— kein Interpret —</option>
                {artists.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.status !== 'PUBLISHED' ? ' (Entwurf)' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-secondary-500">
                Interpreten pflegen Sie unter „Interpreten". Der angezeigte Name wird daraus abgeleitet.
              </p>
            </div>
            <div>
              <label htmlFor={`${feldId}-album`} className="block text-sm font-medium text-secondary-700 mb-2">
                Album
              </label>
              <input
                id={`${feldId}-album`}
                type="text"
                value={formData.album || ''}
                onChange={(e) => handleInputChange('album', e.target.value)}
                className="input w-full"
                placeholder="Album..."
              />
            </div>
            <div>
              <label htmlFor={`${feldId}-sprache`} className="block text-sm font-medium text-secondary-700 mb-2">
                Sprache
              </label>
              <input
                id={`${feldId}-sprache`}
                type="text"
                value={formData.language || ''}
                onChange={(e) => handleInputChange('language', e.target.value)}
                className="input w-full"
                placeholder="Sprache..."
              />
            </div>
          </div>

          {/* Dauer */}
          <div>
            <label htmlFor={`${feldId}-dauer`} className="block text-sm font-medium text-secondary-700 mb-2">
              Dauer
            </label>
            <input
              id={`${feldId}-dauer`}
              type="text"
              value={formData.duration || ''}
              onChange={(e) => handleInputChange('duration', e.target.value)}
              className="input w-full"
              placeholder="z.B. 30 Minuten, 1 Stunde, etc."
            />
          </div>

          {/* Intro-Ende — die Marke, aus der der Player den Knopf „Intro überspringen" baut.
              Ein Zahlenfeld je Titel statt einer Kapitelspur: der Vorspann ist unterschiedlich
              lang und fehlt bei vielen Titeln ganz; eine WebVTT-Spur wäre bei 300 Einzeltiteln
              mehr Pflege als Nutzen. */}
          <div>
            <label htmlFor={`${feldId}-intro-ende`} className="block text-sm font-medium text-secondary-700 mb-2">
              Intro-Ende (Sekunden)
            </label>
            <input
              id={`${feldId}-intro-ende`}
              type="number"
              min={0}
              step={1}
              value={formData.introEndSeconds || ''}
              onChange={(e) => handleInputChange('introEndSeconds', e.target.value)}
              className="input w-full"
              placeholder="z.B. 12"
            />
            <p className="mt-1 text-xs text-secondary-500">
              Der Knopf „Intro überspringen" erscheint im Player nur, wenn hier ein Wert steht — er springt
              genau an diese Sekunde. Hat der Titel keinen Vorspann, bleibt das Feld leer.
            </p>
          </div>

          {/* Kopfhörer */}
          <div>
            <label htmlFor={`${feldId}-kopfhoerer`} className="block text-sm font-medium text-secondary-700 mb-2">
              Kopfhörer
            </label>
            <select
              id={`${feldId}-kopfhoerer`}
              value={formData.headphones || HeadphonesRecommendation.NO}
              onChange={(e) => handleInputChange('headphones', e.target.value as HeadphonesRecommendation)}
              className="input w-full cursor-pointer"
            >
              <option value={HeadphonesRecommendation.RECOMMENDED}>
                {HeadphonesLabels[HeadphonesRecommendation.RECOMMENDED]}
              </option>
              <option value={HeadphonesRecommendation.NOT_NECESSARY}>
                {HeadphonesLabels[HeadphonesRecommendation.NOT_NECESSARY]}
              </option>
              <option value={HeadphonesRecommendation.NO}>
                {HeadphonesLabels[HeadphonesRecommendation.NO]}
              </option>
            </select>
          </div>
        </section>

        {/* Sektion 4: Klassifizierung */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Klassifizierung</h2>
          {/* Themen-Kategorien */}
          <div>
            {/* ⚠️ Kein `<label htmlFor>`: darunter steht KEIN einzelnes Feld, sondern eine Liste
                von Kontrollkästchen (jedes trägt seine eigene, umschließende Beschriftung). Ein
                `htmlFor` müsste sich eines davon aussuchen und benennte damit das Falsche. Richtig
                ist die Gruppe — `role="group"` plus `aria-labelledby`. */}
            <span id={`${feldId}-themen`} className="block text-sm font-medium text-secondary-700 mb-2">
              Themen-Kategorien
            </span>
            <div role="group" aria-labelledby={`${feldId}-themen`}>
              <TopicCategoryPicker
                selected={formData.topicCategories || []}
                onToggle={handleSubTopicToggle}
              />
            </div>
          </div>

          {/* Tags — die Bedienung wohnt seit dem 2026-09-08 EINMAL in `SchlagwortFeld`.
              Vorher stand dieser Block hier und in der Schwester-Vorlage von Hand, gemessen
              zeichengleich (Kommentare geleert, gediffed: Exit 0; Positivkontrolle mit einem
              geänderten Zeichen: Exit 1). Gehoben wurde er, weil das MindBook-Formular eine
              dritte Kopie gebraucht hätte. */}
          <SchlagwortFeld
            werte={formData.tags || []}
            onÄndern={(tags) => setFormData((prev) => ({ ...prev, tags }))}
          />
        </section>

        {/* Sektion 5: Veröffentlichung & Flags */}
        <section className="card p-4 md:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-secondary-900 mb-1">Veröffentlichung &amp; Flags</h2>
          {/* Zugriff (einziges Feld: Premium an = Premium, aus = Free) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <PremiumFeld
              wert={formData.isPremium}
              onÄndern={(wert) => handleInputChange('isPremium', wert)}
              gesperrt={!darfModerieren}
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor={`${feldId}-status`} className="block text-sm font-medium text-secondary-700 mb-2">
              Status
            </label>
            {/* Wählbar sind nur die drei Stufen aus `STATUS_WAEHLBAR` — „Abgelehnt" vergibt die
                Prüfung über `POST /api/content/:id/reject` (ADMIN/MODERATOR), nicht der Verfasser.
                Ist der Datensatz bereits abgelehnt, steht die Stufe GESPERRT dabei: ohne sie zeigte
                das Auswahlfeld eine leere Zeile, und der Zustand wäre unsichtbar. Begründung und
                Texte: `STATUS_WAEHLBAR` / `STATUS_ANZEIGE` in `types/index.ts`. */}
            <select
              id={`${feldId}-status`}
              value={formData.status || ContentStatus.DRAFT}
              onChange={(e) => handleInputChange('status', e.target.value as ContentStatus)}
              aria-describedby={darfModerieren ? undefined : `${feldId}-status-hinweis`}
              className="input w-full cursor-pointer"
            >
              {STATUS_WAEHLBAR.map((stufe) => (
                <option key={stufe} value={stufe} disabled={stufeGesperrt(stufe, darfModerieren)}>
                  {stufenText(stufe, darfModerieren, bestandStatus)}
                </option>
              ))}
              {formData.status === ContentStatus.REJECTED && (
                <option value={ContentStatus.REJECTED} disabled>
                  {STATUS_ANZEIGE[ContentStatus.REJECTED]?.text ?? ContentStatus.REJECTED}
                </option>
              )}
            </select>
            {!darfModerieren && (
              <ModerationsHinweis id={`${feldId}-status-hinweis`} warnung={rückfall}>
                {rückfall ? MODERATIONS_TEXTE.rückfallHinweis : MODERATIONS_TEXTE.statusHinweis}
              </ModerationsHinweis>
            )}
          </div>
        </section>

        {/* Form Actions */}
        <div className="fixed bottom-0 right-0 left-64 z-30 flex justify-end items-center gap-3 px-6 lg:px-8 py-3 md:py-4 bg-secondary-50/95 backdrop-blur-sm border-t border-secondary-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          {/* Gefahren-Aktion: Löschen — nur im Edit-Modus, klar abgesetzt links */}
          {/* Löschen ist Moderation — siehe `AudioForm`, BACKLOG `cms-listenknoepfe-redaktion`. */}
          {videoId && onDelete && darfModerieren && (
            <button
              type="button"
              onClick={() => onDelete(videoId)}
              className="mr-auto inline-flex items-center gap-2 rounded-lg border border-error-300 px-4 py-2 text-sm font-medium text-error-600 hover:bg-error-50 hover:border-error-400 focus:outline-none focus:ring-2 focus:ring-error-500 disabled:opacity-50"
              disabled={isLoading}
            >
              <TrashIcon className="w-4 h-4" />
              Video löschen
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline btn-md"
            disabled={isLoading}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-md"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner w-4 h-4 mr-2" />
                Speichern...
              </>
            ) : (
              'Video speichern'
            )}
          </button>
        </div>
      </form>

      {/* Bunny.net File Picker */}
      <BunnyFilePicker
        isOpen={showBunnyPicker}
        onClose={() => setShowBunnyPicker(false)}
        onSelect={handleBunnyFileSelect}
        fileType={bunnyPickerContext.type}
        allowUrlInput={true}
        title={
          bunnyPickerContext.type === 'image' 
            ? (bunnyPickerContext.field === 'cover' ? 'Cover Bild von Bunny.net auswählen' : 'Thumbnail von Bunny.net auswählen')
            : 'Video von Bunny.net auswählen'
        }
      />
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </>
  );
};
